import { useMemo } from "react";

import { useSearchParams } from "@remix-run/react";

import { useAnonymous } from "~/hooks/use-anonymous";

/**
 * Who is looking at this page.
 *
 * AIDEV-NOTE: This replaces the old split-brain of `isPublicSession = !!sessionToken`
 * (a URL fact) versus `useAnonymous()` (a Supabase-session fact). They answer DIFFERENT
 * questions and a logged-in user opening a share link satisfies one and not the other, so
 * mixing them in one component rendered contradictory state. Derive from here instead and
 * pick the predicate that matches what the call site actually means.
 *
 * - `owner`        — real account, reached the page through their own access.
 * - `token-viewer` — real account, but arrived via a share token. Read-only.
 * - `anonymous`    — no real account (no Supabase session, or an anonymous one).
 *
 * This mirrors the server: `session.server.ts` resolves a valid `?sessionToken` to
 * `public-authenticated` even when a real user session is present, so a token in the URL
 * always demotes the viewer to read-only regardless of who they are signed in as.
 */
export type ViewerState = "owner" | "token-viewer" | "anonymous";

export interface ViewerStateResult {
    viewerState: ViewerState;
    /**
     * Reached this page through a share token. The direct replacement for the old
     * `isPublicSession`: use it for "not the owner" / read-only / no-mutation decisions.
     */
    isShareTokenView: boolean;
    /**
     * No real account at all. The direct replacement for the old `isAnonymous`: use it for
     * "there is nobody to email / bill / attribute this to" and for sign-up prompts.
     * NOT the same as `isShareTokenView` — a signed-in user can be a token viewer.
     */
    isAnonymousViewer: boolean;
    /** Signed-in AND not on a share link — the only state that may mutate the session. */
    isOwnerViewer: boolean;
    isLoading: boolean;
}

export const useViewerState = (): ViewerStateResult => {
    const [searchParams] = useSearchParams();
    const { isAnonymous, isLoading } = useAnonymous();

    const isShareTokenView = !!searchParams.get("sessionToken");

    return useMemo(() => {
        const viewerState: ViewerState = isAnonymous
            ? "anonymous"
            : isShareTokenView
              ? "token-viewer"
              : "owner";

        return {
            viewerState,
            isShareTokenView,
            isAnonymousViewer: viewerState === "anonymous",
            isOwnerViewer: viewerState === "owner",
            isLoading,
        };
    }, [isAnonymous, isShareTokenView, isLoading]);
};
